import {
  Button,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Image,
  Select,
  Spin,
  Table,
  Tag,
  Typography,
  Input,
  InputNumber,
  message,
} from "antd";
import { LoadingScreen, NoAccessScreen } from "../../components";
import { AppFeature, IMAGE_URL } from "../../Constant";
import { usePagePermission } from "../../Hook";
import { useEffect, useState } from "react";
import { Eye, Pencil, Search } from "lucide-react";
import { useAuthChecker } from "../../Context";
import {
  FetchCustomerData_api,
  FetchPaymentDetails_api,
  FetchPaymentType_api,
  UpdatePayments,
} from "../../Api";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";

const { Option } = Select;
const { Title, Text } = Typography;

export const PaymentHistoryPage = () => {
  const { isLoading, hasPermission } = usePagePermission(
    AppFeature.PAYMENT_HISTORY,
    ["admin", "cashier", "manager"],
  );

  const [customerData, setCustomerData] = useState<any>([]);
  const [paymentData, setPaymentData] = useState<any>([]);
  const [addedCustomerData, setAddedCustomeData] = useState<any>([]);
  const [form] = Form.useForm<any>();
  const [searchKeywordForCustomerSearch, setSearchKeywordForCustomerSearch] =
    useState("");
  const [debouncedQueryForCustomerSearch] = useDebounce(
    searchKeywordForCustomerSearch,
    500,
  );

  // Table Search and Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [tableSearchText, setTableSearchText] = useState("");
  const [totalPayments, setTotalPayments] = useState(0);

  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [paymentTypes, setPaymentTypes] = useState<any[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);

  const { user, isAdmin } = useAuthChecker();

  const totalAmount = paymentData.reduce((sum: number, item: any) => {
    return sum + parseFloat(item.amount || 0);
  }, 0);

  const { refetch: FetchCustomerData, isFetching: isFetchingCustomers } =
    useQuery<any>({
      queryKey: ["searchQueryCustomer"],
      queryFn: async () => {
        const response: any = await FetchCustomerData_api(
          debouncedQueryForCustomerSearch.trim(),
        );
        setCustomerData(response.customerData);
        return response;
      },
      enabled: true,
      refetchOnWindowFocus: false,
    });

  const [isCreditedFilter, setIsCreditedFilter] = useState("");

  const {
    data: paymentResponse,
    isFetching: isFetchingPaymentData,
    refetch: refetchPayments,
  } = useQuery({
    queryKey: [
      "paymentHistory",
      addedCustomerData?.id || "all",
      page,
      pageSize,
      tableSearchText,
      isCreditedFilter,
    ],
    queryFn: async () => {
      const cid = addedCustomerData?.id || "";
      const res = await FetchPaymentDetails_api(
        cid,
        page,
        pageSize,
        tableSearchText,
        isCreditedFilter,
      );
      return res;
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (paymentResponse) {
      setPaymentData(paymentResponse.paymentDetails || []);
      setTotalPayments(paymentResponse.total || 0);
    }
  }, [paymentResponse]);

  const handleViewDetails = (record: any) => {
    setSelectedRecord(record);
    setDrawerOpen(true);
  };

  const handleEditDetails = async (record: any) => {
    setSelectedRecord(record);

    let types = paymentTypes;
    if (types.length === 0) {
      const res = await FetchPaymentType_api();
      console.log(res);

      if (res?.isDataSet) {
        setPaymentTypes(res?.paymentType);
      }
    }

    editForm.setFieldsValue({
      payment_type_id: record.payment_type_id,
      slip_id: record.slip_id,
      slip_date: record.slip_date,
      amount: record.amount,
    });

    setEditDrawerOpen(true);
  };

  const handleUpdatePayment = async (values: any) => {
    setIsUpdating(true);
    try {
      const payload = {
        paymentId: selectedRecord.id,
        userId: user?.userId,
        formData: {
          ...JSON.parse(selectedRecord.dataobj || "{}"),
          payment_type_id: values.payment_type_id,
          slip_id: values.slip_id,
          slip_date: values.slip_date,
          amount: values.amount,
        },
      };

      const res = await UpdatePayments(payload);
      if (res?.isDataUpdated) {
        message.success(res.msg);
        setEditDrawerOpen(false);
        refetchPayments();
      } else {
        message.error(res.msg || "Failed to update payment");
      }
    } catch (error) {
      message.error("Error updating payment");
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    FetchCustomerData();
  }, [debouncedQueryForCustomerSearch]);

  const columns = [
    {
      title: "Slip ID",
      dataIndex: "slip_id",
      key: "slip_id",
      render: (text: string, record: any) => (
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-800">{text}</span>
          <Button
            size="small"
            type="link"
            onClick={() => handleViewDetails(record)}
            icon={<Eye size={16} />}
          />
          {isAdmin && (
            <Button
              size="small"
              type="link"
              className="text-orange-500 hover:text-orange-600"
              onClick={() => handleEditDetails(record)}
              icon={<Pencil size={16} />}
            />
          )}
        </div>
      ),
    },
    {
      title: "Payment Type",
      dataIndex: "payment_type_name",
      key: "payment_type_name",
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Date",
      dataIndex: "slip_date",
      key: "slip_date",
    },
    {
      title: "Amount (LKR)",
      dataIndex: "amount",
      key: "amount",
      render: (text: string) => (
        <span className="font-semibold text-green-600">
          Rs{" "}
          {parseFloat(text).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            minimumIntegerDigits: 1,
          })}
        </span>
      ),
    },
    {
      title: "Credited",
      dataIndex: "is_credited",
      key: "is_credited",
      render: (text: string) => (
        <Tag color={text === "1" ? "green" : "red"}>
          {text == "1" ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Customer",
      dataIndex: "customer_name",
      key: "customer_name",
    },
    {
      title: "Added On",
      dataIndex: "added_date_time",
      key: "added_date_time",
    },
  ];

  if (isLoading) {
    return <LoadingScreen messageQueue={"Loading..."} />;
  }

  if (!hasPermission) {
    return <NoAccessScreen />;
  }

  return (
    <>
      <div className="w-full min-h-screen bg-gray-50 p-6">
        <div className="flex justify-between items-center px-6">
          <h1 className="text-2xl font-semibold">Payment History</h1>
        </div>
        <div className="flex flex-col space-y-6">
          <div className="flex flex-col sm:flex-row gap-4 bg-white p-6 rounded-xl shadow-md items-center justify-between">
            <div className="flex items-end flex-col sm:flex-row gap-4 w-full max-w-xl">
              <div className="flex-1 flex items-end space-x-2">
                <div>
                  <label className="block text-sm mb-1 text-gray-800">
                    Filter by Customer
                  </label>
                  <Select
                    showSearch
                    loading={isFetchingCustomers}
                    placeholder="Search Customer..."
                    optionFilterProp="children"
                    className="w-[250px]"
                    onSearch={(value: string) =>
                      setSearchKeywordForCustomerSearch(value)
                    }
                    onChange={(value) => {
                      const selectedCustomer = customerData?.find(
                        (customer: any) => customer.id === value,
                      );
                      setAddedCustomeData(selectedCustomer);
                      setPage(1);
                    }}
                    allowClear
                    onClear={() => {
                      setAddedCustomeData(null);
                      setPage(1);
                    }}
                    notFoundContent={
                      isFetchingCustomers ? (
                        <Spin size="small" />
                      ) : searchKeywordForCustomerSearch ? (
                        <div className="px-2 py-1 text-gray-500">
                          No data Found
                        </div>
                      ) : null
                    }
                  >
                    <Option key={"id"} value={""}>
                      All customers
                    </Option>
                    {customerData?.map((data: any) => (
                      <Option key={data.id} value={data.id}>
                        {data.name} - {data.mobile}
                      </Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block text-sm mb-1 text-gray-800">
                    Filter by Credited
                  </label>
                  <Select
                    placeholder="Filter by Credited..."
                    optionFilterProp="children"
                    className="w-[250px]"
                    onChange={(value) => {
                      setIsCreditedFilter(value);
                      setPage(1);
                    }}
                    allowClear
                  >
                    <Option key={"1"} value={"1"}>
                      Yes
                    </Option>
                    <Option key={"0"} value={"0"}>
                      No
                    </Option>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <Table
              loading={isFetchingPaymentData}
              columns={columns}
              dataSource={paymentData}
              rowKey="id"
              bordered={false}
              pagination={{
                current: page,
                pageSize: pageSize,
                total: totalPayments,
                onChange: (p, ps) => {
                  setPage(p);
                  setPageSize(ps);
                },
                showSizeChanger: true,
              }}
              className="rounded-lg overflow-x-auto"
            />

            {addedCustomerData?.id && !isFetchingPaymentData && (
              <div className="text-right mt-4">
                <Text strong className="text-lg text-blue-700">
                  Total Amount (Loaded): Rs{" "}
                  {totalAmount.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Text>
              </div>
            )}
          </div>
        </div>

        {/* 🔍 Drawer for Details */}
        <Drawer
          title={
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold">Payment Details</span>
            </div>
          }
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={500}
        >
          {selectedRecord ? (
            <>
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label="Customer">
                  {selectedRecord.customer_name}
                </Descriptions.Item>
                <Descriptions.Item label="Payment Type">
                  {selectedRecord.payment_type_name}
                </Descriptions.Item>
                <Descriptions.Item label="Slip ID">
                  {selectedRecord.slip_id}
                </Descriptions.Item>
                <Descriptions.Item label="Date">
                  {selectedRecord.slip_date}
                </Descriptions.Item>
                <Descriptions.Item label="Amount (LKR)">
                  Rs{" "}
                  {parseFloat(selectedRecord.amount).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                  })}
                </Descriptions.Item>
              </Descriptions>

              {selectedRecord.imageName && (
                <>
                  <Divider />
                  <Image
                    src={`${IMAGE_URL}/upload/${selectedRecord.imageName}`}
                    alt="Payment Slip"
                    width="100%"
                    className="rounded-xl"
                  />
                </>
              )}

              {/* Render parsed dataObj */}
              {selectedRecord.dataobj && (
                <>
                  <Divider />
                  <Title level={5}>Additional Information</Title>
                  <div className="space-y-1 text-sm text-gray-700">
                    {Object.entries(JSON.parse(selectedRecord.dataobj)).map(
                      ([key, value]: any) => (
                        <div
                          key={key}
                          className="flex justify-between border-b py-1"
                        >
                          <span className="capitalize font-medium text-gray-600">
                            {key.replace(/_/g, " ")}
                          </span>
                          <span className="text-right text-gray-800">
                            {value || "—"}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                </>
              )}
            </>
          ) : (
            <Spin tip="Loading details..." />
          )}
        </Drawer>

        <Drawer
          title="Edit Payment Details"
          open={editDrawerOpen}
          onClose={() => setEditDrawerOpen(false)}
          width={500}
        >
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleUpdatePayment}
          >
            <Form.Item
              name="payment_type_id"
              label="Payment Type"
              rules={[
                { required: true, message: "Please select payment type" },
              ]}
            >
              <Select placeholder="Select Payment Type">
                {paymentTypes?.map((type: any) => (
                  <Option key={type.id} value={type.id}>
                    {type.type}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="slip_id"
              label="Slip ID / Reference"
              rules={[{ required: true, message: "Please enter reference" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="slip_date"
              label="Date"
              rules={[{ required: true, message: "Please enter date" }]}
            >
              <Input placeholder="YYYY-MM-DD" />
            </Form.Item>

            <Form.Item
              name="amount"
              label="Amount (LKR)"
              rules={[{ required: true, message: "Please enter amount" }]}
            >
              <InputNumber
                className="w-full"
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value!.replace(/\$\s?|(,*)/g, "")}
              />
            </Form.Item>

            <Form.Item className="mt-8">
              <div className="flex gap-4">
                <Button
                  className="flex-1"
                  onClick={() => setEditDrawerOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="flex-1"
                  loading={isUpdating}
                >
                  Save Changes
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Drawer>
      </div>
    </>
  );
};
